/** Route path for the forced permit-exam booking gate. */
export const PERMIT_EXAM_GATE_PATH = "/permit-exam-gate";

/**
 * Query param carrying the lesson path the student was heading to. The gate
 * navigates here (with `replace`) once the THEORY_EXAM milestone is written.
 */
export const GATE_RETURN_PARAM = "next";

/**
 * Lesson whose completion write immediately preceded the redirect. The gate
 * uses it to bridge the short Firestore snapshot race after `markCompleted()`.
 */
export const GATE_COMPLETED_LESSON_PARAM = "completed";

/**
 * Query param recording which study action diverted the student here:
 * `lesson_advance` (the original `navigateForward` divert) or `quiz_start`
 * (opening the practice-exam screen). Carried onto the Gate Viewed event so
 * readouts can segment by surface.
 *
 * `lesson_start` (opening a lesson page) is RETIRED as of 2026-10-06 — see
 * `GATE_TRIGGERS`. It stays in the union so historical events still parse and
 * so a gate URL already open in a student's tab keeps resolving.
 */
export const GATE_TRIGGER_PARAM = "trigger";

export const GATE_TRIGGERS = [
  "lesson_advance",
  "lesson_start",
  "quiz_start",
] as const;

export type PermitGateTrigger = typeof GATE_TRIGGERS[number];

/**
 * The study-action triggers `useStudyActionPermitGate` may divert from —
 * everything except the lesson advance, which `useResourceNavigation` owns.
 *
 * Only `quiz_start` is emitted today. `lesson_start` is retained for the
 * historical events and in-flight URLs described on `GATE_TRIGGER_PARAM`.
 */
export type StudyActionGateTrigger = Exclude<
  PermitGateTrigger,
  "lesson_advance"
>;

/**
 * Completed-lesson count that opens the gate. The CDL curriculum is 21 lessons,
 * so 10 is the midpoint.
 *
 * The check is `>=`, not `===`. Two reasons: (1) `useResourceProgress` is a live
 * snapshot, so the count can lag the `markCompleted` write that precedes
 * `navigateForward` by a tick, and an equality check would miss the gate
 * entirely and never fire again; (2) students already past lesson 10 when the
 * experiment launches should still be gated once. The gate is self-limiting
 * either way — it stops firing the moment a THEORY_EXAM milestone exists.
 *
 * Because of `>=` (and the 2026-09-08 surface expansion, which gates returning
 * students up to and including course completers), the momentum screen is shown
 * well past the midpoint. Its headline is therefore chosen from the actual
 * completed fraction, not hardcoded — see `utils/gateMomentumStage.ts`.
 */
export const GATE_AFTER_COMPLETED_LESSONS = 10;

/** Title used for the THEORY_EXAM milestone this flow writes. */
export const CLP_MILESTONE_TITLE = "Obtain the Class A Permit";

/**
 * Which screen a booking-flow report came from. The forced gate and the
 * standalone `/permit-test` page render the same self-booking step, so every
 * stamp they write carries this to keep the two apart in a readout.
 */
export type PermitGateSurface = "gate" | "permit_test_page";
